import React, { useState, useEffect, useCallback } from 'react';
import { ADMIN_ORGANIZATION_ID } from '../config/api';
import { Box } from '@mui/material';
import {
  WalteroCard,
  WalteroButton,
  WalteroTypography,
  WalteroTabs,
  WalteroTabPanel
} from './waltero';
import { hasOrganizationAccess } from '../utils/auth';
import DataCorrectionModal from './DataCorrectionModal';
import VisualReview from './VisualReview';
import './ReportTab.css';

// Import new modular components
import { ReportDataProvider, useReportData } from '../context/ReportDataContext';
import SummaryCards from './report/SummaryCards';
import DevicesMap from './report/DevicesMap';
import DailyConsumption from './report/DailyConsumption';
import HourlyUsage from './report/HourlyUsage';
import SensorSummary from './report/SensorSummary';
import SensorGraphs from './report/SensorGraphs';
import ImageModal from './report/ImageModal';
import ConfigHoverOverlay from './report/ConfigHoverOverlay';

// Internal component that uses the context
const ReportTabContent = ({ selectedOrgId, startDate, endDate }) => {
  console.log('🎯 ReportTab rendered with props:', { selectedOrgId, startDate, endDate });
  
  const { loadReportData, isLoading, error, reportData, summary } = useReportData();
  const [activeTab, setActiveTab] = useState('daily');
  const [zoomLevel, setZoomLevel] = useState('daily'); // daily, hourly, 15min
  
  // Data correction modal state
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [selectedSensorForCorrection, setSelectedSensorForCorrection] = useState(null);
  
  // Color coding control state
  const [showStatusColors, setShowStatusColors] = useState(true);

  // Image modal state for sensor summary
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Configuration hover overlay state
  const [configHover, setConfigHover] = useState({
    show: false,
    data: null,
    position: { x: 0, y: 0 }
  });

  // Handle image modal open - memoized to prevent recreation
  const handleImageModalOpen = useCallback((image) => {
    setSelectedImage(image);
    setImageModalOpen(true);
  }, []);

  // Handle image modal close - memoized to prevent recreation
  const handleImageModalClose = useCallback(() => {
    setImageModalOpen(false);
    setSelectedImage(null);
  }, []);
  
  // Handle configuration hover overlay
  const handleConfigHover = useCallback((event, configData, fieldName) => {
    if (event && configData) {
      setConfigHover({
        show: true,
        data: { ...configData, fieldName },
        position: { x: event.clientX, y: event.clientY }
      });
    } else {
      setConfigHover({ show: false, data: null, position: { x: 0, y: 0 } });
    }
  }, []);

  // Data correction modal functions
  const openCorrectionModal = useCallback((device) => {
    console.log('🔧 Opening correction modal for sensor:', device.serialNumber);
    setSelectedSensorForCorrection(device);
    setCorrectionModalOpen(true);
  }, []);

  const closeCorrectionModal = useCallback((dataWasUpdated = false) => {
    console.log('🔧 Closing correction modal, data updated:', dataWasUpdated);
    setCorrectionModalOpen(false);
    setSelectedSensorForCorrection(null);
    
    if (dataWasUpdated) {
      // Refresh the report data to show updated values
      console.log('🔄 Refreshing report data due to corrections');
      loadReportData(selectedOrgId, startDate, endDate, zoomLevel);
    }
  }, [loadReportData, selectedOrgId, startDate, endDate, zoomLevel]);
  
  const handleDataUpdated = useCallback(() => {
    // Refresh the report data when corrections are made (without closing modal)
    console.log('🔄 Refreshing report data due to corrections (modal stays open)');
    loadReportData(selectedOrgId, startDate, endDate, zoomLevel);
  }, [loadReportData, selectedOrgId, startDate, endDate, zoomLevel]);

  const handleZoomChange = (newZoomLevel) => {
    setZoomLevel(newZoomLevel);
    // Don't auto-reload - user must press refresh button
  };

  // Tab navigation handlers for accessibility
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);
  
  // Tab configuration for WalteroTabs component
  const reportTabs = [
    { id: 'daily', label: 'Daily Consumption' },
    { id: 'hourly', label: 'Hourly Usage' },
    { id: 'sensorSummary', label: 'Sensor Summary' }
  ];
  
  // Add admin-only tabs if user has access
  if (hasOrganizationAccess(ADMIN_ORGANIZATION_ID)) {
    reportTabs.push(
      { id: 'sensors', label: 'Sensor Graphs', adminOnly: true },
      { id: 'visualReview', label: 'Visual Review', adminOnly: true }
    );
  }

  // Auto-load data when component mounts or when dependencies change
  // This is triggered when the "Load Report Data" button is clicked in App.js
  useEffect(() => {
    console.log('🔄 Auto-load useEffect triggered with:', { selectedOrgId, startDate, endDate });
    if (selectedOrgId && selectedOrgId !== 'undefined' && selectedOrgId !== undefined && startDate && endDate) {
      console.log('✅ Auto-load - calling loadReportData');
      loadReportData(selectedOrgId, startDate, endDate, zoomLevel);
    } else {
      console.log('❌ Auto-load - missing required props');
      console.log('🔍 Debug values:', { selectedOrgId, startDate, endDate });
    }
  }, [selectedOrgId, startDate, endDate, zoomLevel, loadReportData]);

  if (isLoading) {
    console.log('🔄 Report tab is loading...');
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="elevated" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography 
            variant="h6" 
            color="primary"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Loading report data...
          </WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  if (error) {
    console.log('❌ Report tab has error:', error);
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: '#c35d2c' }}>
          <WalteroTypography 
            variant="h6" 
            sx={{ color: '#c35d2c' }}
            role="alert"
            aria-live="assertive"
          >
            Error: {error}
          </WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  // Early return if no organization is selected
  if (!selectedOrgId || selectedOrgId === 'undefined' || selectedOrgId === undefined || selectedOrgId === '') {
    console.log('⚠️ Report tab: no organization selected');
    console.log('🔍 selectedOrgId type:', typeof selectedOrgId, 'value:', selectedOrgId);
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">Please select an organization to view usage reports</WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  // Early return if no dates are provided
  if (!startDate || !endDate) {
    console.log('⚠️ Report tab: no dates provided');
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">Please select start and end dates to view usage reports</WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  console.log('🎯 Report tab rendering with:', {
    reportDataLength: reportData.length,
    summary: summary,
    selectedOrgId: selectedOrgId
  });

  return (
    <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh' }}>
      {/* Summary Cards */}
      <SummaryCards summary={summary} />

      {/* Map View */}
      {reportData.length > 0 && (
        <div className="map-section">
          <DevicesMap devices={reportData} />
        </div>
      )}

      {/* Tab Navigation */}
      <WalteroTabs
        tabs={reportTabs}
        value={activeTab}
        onChange={handleTabChange}
        ariaLabel="Report data views"
        showAdminBadges={true}
      />

      {/* Tab Content */}
      <div className="report-content">
        <WalteroTabPanel value={activeTab} index="daily">
          <DailyConsumption />
        </WalteroTabPanel>
        
        <WalteroTabPanel value={activeTab} index="hourly">
          <HourlyUsage />
        </WalteroTabPanel>
        
        <WalteroTabPanel value={activeTab} index="sensorSummary">
          <SensorSummary 
            onImageClick={handleImageModalOpen} 
            onConfigHover={handleConfigHover}
          />
        </WalteroTabPanel>
        
        {hasOrganizationAccess(ADMIN_ORGANIZATION_ID) && (
          <>
            <WalteroTabPanel value={activeTab} index="sensors">
              <SensorGraphs
                showStatusColors={showStatusColors}
                onShowStatusColorsChange={setShowStatusColors}
                onOpenCorrectionModal={openCorrectionModal}
              />
            </WalteroTabPanel>
            
            <WalteroTabPanel value={activeTab} index="visualReview">
              <div className="visual-review-tab-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <WalteroTypography variant="h5" component="h3" sx={{ margin: 0 }}>
                    Visual Review
                  </WalteroTypography>
                </div>
                <VisualReview 
                  reportData={reportData} 
                  selectedOrgId={selectedOrgId} 
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </WalteroTabPanel>
          </>
        )}
      </div>

      {/* Configuration Hover Overlay */}
      <ConfigHoverOverlay configHover={configHover} />

      {/* Image Modal */}
      <ImageModal 
        isOpen={imageModalOpen}
        image={selectedImage}
        onClose={handleImageModalClose}
      />

      {reportData.length === 0 && !isLoading && (
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">
            No usage data available for the selected organization and date range.
          </WalteroTypography>
        </WalteroCard>
      )}

      {/* Data Correction Modal */}
      {correctionModalOpen && selectedSensorForCorrection && (
        <DataCorrectionModal
          sensor={selectedSensorForCorrection}
          organizationId={selectedOrgId}
          startDate={startDate}
          endDate={endDate}
          onClose={closeCorrectionModal}
          onDataUpdated={handleDataUpdated}
        />
      )}
    </Box>
  );
};

// Main wrapper component with provider
const ReportTab = ({ selectedOrgId, startDate, endDate }) => {
  return (
    <ReportDataProvider>
      <ReportTabContent 
        selectedOrgId={selectedOrgId}
        startDate={startDate}
        endDate={endDate}
      />
    </ReportDataProvider>
  );
};

export default ReportTab;